import React, { useContext, useMemo } from "react";
import { Container, Divider, Grid, makeStyles, Paper, Typography, withStyles } from "@material-ui/core";
import banner from '../../assets/globe_upsidedown.png'
import { ReactComponent as ShieldIcon } from '../../assets/shield-icon.svg';
import { ReactComponent as AircraftIcon } from '../../assets/aircraft-icon-small.svg';
import { ReactComponent as CalendarIcon } from '../../assets/calendar-icon.svg';
import { ReactComponent as TimeIcon } from '../../assets/time-icon.svg';
import { ReactComponent as UserIcon } from '../../assets/user-icon-small.svg';
import { QuoteContextStore } from '../../context/QuoteContext/QuoteContext';
import { readableHullCoverage } from '../PurchaseFlow/Coverages/types';
import { extractMainLiability, extractPassengerLiability } from "../../services/utils";
import globalLogo from '../../assets/global-colored.png'
import { ControlledDatePicker } from '../Core/Form/Datepickers';
import { Edit } from "@material-ui/icons";


const useBannerStyle = makeStyles((theme)=> ({
    root: {
        backgroundImage: 'linear-gradient(to right, #030a2d , #1c3b6d)',
        backgroundRepeat: 'no-repeat',
        borderRadius: '35px 35px 0 0',
        height: '120px',
        position: 'relative'
    },
    image: {
        backgroundImage: `url(${banner})`,
        backgroundSize: 'cover',
        position: 'absolute',
        width: '100%',
        height: '100%',
    },
    titleContainer: {
        position: 'absolute',
        transform: 'translate(0, 50%)'
    },
    title: {
        fontFamily: 'Poppins-Bold',
        color: '#55ccde',
        fontSize: '20px',
    },
    subtitle: {
        fontFamily: 'Poppins-Regular',
        color: 'white',
        fontSize: '12px',
        fontWeight: 200
    }
}));

const CustomPaper = withStyles(theme => ({
    root: {
        borderRadius: '35px 0 35px 35px',
        position: 'relative',
        top: '-20px'
    }
}))(Paper);

const CardTypography = withStyles(theme => ({
    root: {
        fontFamily: 'Poppins-Regular',
        fontSize: '0.95em',
        color: '#6e707a',
        width: '100%',
        [theme.breakpoints.down('xs')]: { 
            fontSize: '0.6em',
        },
    }
}))(Typography);

const SubCardTypography = withStyles(theme => ({
    root: {
        width: '100%',
        fontFamily: 'Poppins-Regular',
        fontSize: '0.8em',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        color: '#164076',
    }
}))(Typography);

const SubCardTotalTypography = withStyles(theme => ({
    root: {
        width: '100%',
        fontFamily: 'Poppins-Bold',
        fontSize: '1em',
        fontWeight: 'bolder',
        color: '#164076',
    }
}))(Typography);


const SummaryCard = () => {
    const bannerClasses = useBannerStyle();
    const quoteContext = useContext(QuoteContextStore);

    const {aircrafts, price_breakdown, total_amount, total_premium, total_surcharge, shouldShowMedicalCoverage, coverages: {selected}} = quoteContext;
    const aircraft = aircrafts[Object.keys(aircrafts)[0]];
    const startDate = new Date(quoteContext.start_date)
    const endDate = new Date(quoteContext.end_date)
    
    const termEnd = useMemo(() => new Date(startDate.getFullYear() + 1, startDate.getMonth() , startDate.getDay()), [quoteContext.start_date]);

    const totalSurcharge = total_surcharge ?? 0.0;
    const totalPremium = useMemo(() => {
        if(!price_breakdown) return 0;
        let total = 0;
        total += price_breakdown.medical_coverage ?? 0
        total += price_breakdown.physical_damage_coverage ?? 0
        total += price_breakdown.liability_coverage ?? 0
        return total;
    }, [price_breakdown])

    return (
        <Grid xs={12}>
            <Grid item className={bannerClasses.root}>
                <div className={bannerClasses.image} />
                <div className={bannerClasses.titleContainer}>
                    <Container>
                        <Typography className={bannerClasses.title}>
                            Your Policy
                        </Typography>
                        <Typography className={bannerClasses.subtitle}>
                            You can always modify all policy details later
                        </Typography>
                    </Container>
                </div>
            </Grid>
            <Grid item>
                <CustomPaper elevation={0} style={{ width: '100%' }}>
                    <Container>
                        <Grid container spacing={4}>
                            <Container className="margin-bottom-10 margin-top-10">
                                <Grid className="margin-top-small margin-bottom-small" container wrap="nowrap">
                                    <Grid container alignItems="center" wrap="nowrap">
                                        <CalendarIcon className="margin-right-10" />
                                        <CardTypography variant="body">
                                            Start Date:
                                        </CardTypography>
                                    </Grid>
                                    <ControlledDatePicker keyboardIcon={<Edit/>} disablePast variant="standard" name="start_date"/>
                                </Grid>
                                <Grid className="margin-top-small margin-bottom-small" container wrap="nowrap">
                                    <Grid container alignItems="center" wrap="nowrap">
                                        <UserIcon className="margin-right-10" />
                                        <CardTypography variant="body">
                                            Name:
                                        </CardTypography>
                                    </Grid>
                                    <CardTypography variant="body" className="textright">{quoteContext.profile?.name} {quoteContext.profile?.surname}</CardTypography>
                                </Grid>
                                <Grid className="margin-top-small margin-bottom-small"  container wrap="nowrap">
                                    <Grid container alignItems="center" wrap="nowrap">
                                        <TimeIcon className="margin-right-10" />
                                        <CardTypography variant="body">
                                            Policy term:
                                        </CardTypography>
                                    </Grid>
                                    <CardTypography variant="body" className="textright">Annual</CardTypography>
                                </Grid>
                                <Grid className="margin-top-small margin-bottom-small"  container wrap="nowrap">
                                    <Grid container alignItems="center" wrap="nowrap">
                                        <AircraftIcon className="margin-right-10" />
                                        <CardTypography variant="body">
                                            Aircraft type:
                                        </CardTypography>
                                    </Grid>
                                    <CardTypography variant="body" className="textright">{`${aircraft?.make}, ${aircraft?.model}, ${aircraft?.year}`}</CardTypography>
                                </Grid>
                                <Grid container alignItems="center" wrap="nowrap" className="margin-bottom-small">
                                    <ShieldIcon className="margin-right-10 summary-icon" />
                                    <Grid container direction="column">
                                        <CardTypography variant="body">
                                            Coverages:
                                        </CardTypography>
                                    </Grid>
                                </Grid>
                                <Grid container alignItems="center" wrap="nowrap">
                                    <div className="summary-icon"/>
                                    <Grid container wrap="nowrap">
                                        <CardTypography variant="body" className="bold">
                                            Physical Damage coverage:
                                        </CardTypography>
                                        { price_breakdown && <CardTypography variant="body" className="textright">
                                        ${(price_breakdown.physical_damage_coverage).toFixed(2).toLocaleString("en-US")}
                                        </CardTypography>}
                                    </Grid>
                                </Grid>
                                <Grid container alignItems="center" wrap="nowrap" className="margin-bottom-small">
                                    <div className="summary-icon"/>
                                    <Grid container wrap="nowrap">
                                        { selected && <CardTypography variant="body">
                                            {readableHullCoverage[selected.physical_damage_coverage]}
                                        </CardTypography>}
                                    </Grid>
                                </Grid>
                                <Grid container alignItems="center" wrap="nowrap">
                                    <div className="summary-icon"/>
                                    <Grid container wrap="nowrap">
                                        <CardTypography variant="body" className="bold">
                                            Liability Coverage:
                                        </CardTypography>
                                        { price_breakdown && <CardTypography variant="body" className="textright">
                                        ${(price_breakdown.liability_coverage).toFixed(2).toLocaleString("en-US")}
                                        </CardTypography>}
                                    </Grid>
                                </Grid>
                                { selected && <>
                                <Grid container alignItems="center" wrap="nowrap">
                                    <div className="summary-icon"/>
                                    <Grid container wrap="nowrap">
                                        <CardTypography variant="body">
                                            Per Accident: {extractMainLiability(selected.liability_coverage)}
                                        </CardTypography>
                                    </Grid>
                                </Grid>
                                <Grid container alignItems="center" wrap="nowrap" className="margin-bottom-small">
                                    <div className="summary-icon"/>
                                    <Grid container wrap="nowrap">
                                        <CardTypography variant="body">
                                            Per Passenger: {extractPassengerLiability(selected.liability_coverage)}
                                        </CardTypography>
                                    </Grid>
                                </Grid>
                                </>}
                                <Grid container alignItems="center" wrap="nowrap" className="margin-bottom-small">
                                    <div className="summary-icon"/>
                                    <Grid container wrap="nowrap">
                                        <CardTypography variant="body">
                                            <span className="bold">Medical Expense: </span>
                                            ${selected.medical_coverage}
                                        </CardTypography>
                                        <CardTypography variant="body" className="textright">
                                            Included
                                        </CardTypography>
                                    </Grid>
                                </Grid>
                                <Grid container alignItems="center" wrap="nowrap" className="margin-bottom-small">
                                    <div className="summary-icon"/>
                                    <Grid container wrap="nowrap">
                                        <CardTypography variant="body">
                                            Underwriter
                                        </CardTypography>
                                        <CardTypography variant="body" className="textright">
                                            Global Aerospace
                                        </CardTypography>
                                        <img src={globalLogo} alt="underwriter" className="summary-global-icon"/>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                <Grid className="margin-top-small margin-bottom-small"  container wrap="nowrap">
                                    <Grid container alignItems="center" wrap="nowrap">
                                        <SubCardTypography variant="body">
                                            State surcharge:
                                        </SubCardTypography>
                                    </Grid>
                                    <SubCardTypography variant="body" className="textright"> ${totalSurcharge.toFixed(2).toLocaleString("en-US")}</SubCardTypography>
                                </Grid>
                                <Grid className="margin-top-small margin-bottom-small"  container wrap="nowrap">
                                    <Grid container alignItems="center" wrap="nowrap">
                                        <SubCardTotalTypography variant="body">
                                            Price:
                                        </SubCardTotalTypography>
                                    </Grid>
                                    <SubCardTotalTypography variant="body" className="textright">${(totalPremium + totalSurcharge).toFixed(2).toLocaleString("en-US")}</SubCardTotalTypography>
                                </Grid>
                            </Container>
                        </Grid>
                    </Container>
                </CustomPaper>
            </Grid>
        </Grid>
    );
}

export default SummaryCard;